<template>
  <div class="basketball-data">
    <div class="bn-head">
      <div class="bn-head__team bn-head__team--home">
        <span class="bn-head__name overflow-text">{{ homeTeamName || '主队' }}</span>
        <img v-if="homeTeamIcon" :src="homeTeamIcon" class="bn-head__badge-img" alt="">
      </div>
      <img
        class="bn-head__vs"
        src="https://images.snsports.cn/Fh0s60h3TTIo_bFUdUJ70y5WYMul"
        alt="VS"
      />
      <div class="bn-head__team bn-head__team--away">
        <img v-if="awayTeamIcon" :src="awayTeamIcon" class="bn-head__badge-img" alt="">
        <span class="bn-head__name overflow-text">{{ awayTeamName || '客队' }}</span>
      </div>
    </div>

    <div v-if="displayRows.length" class="bn-rows">
      <div v-for="(row, idx) in displayRows" :key="row.rowKey || idx" class="bn-row">
        <div class="bn-row__title">{{ row.title }}</div>
        <div class="bn-row__bar-line">
          <span class="bn-row__home-val">{{ rowDisplayValue(row, 'home') }}</span>
          <div class="bn-bar-track">
            <div class="bn-bar-half bn-bar-half--home">
              <div
                class="bn-bar-seg bn-bar-seg--home"
                :class="barSegClass(row, 'home')"
                :style="barHalfSegStyle(row, 'home')"
              ></div>
            </div>
            <div class="bn-bar-half bn-bar-half--away">
              <div
                class="bn-bar-seg bn-bar-seg--away"
                :class="barSegClass(row, 'away')"
                :style="barHalfSegStyle(row, 'away')"
              ></div>
            </div>
          </div>
          <span class="bn-row__away-val">{{ rowDisplayValue(row, 'away') }}</span>
        </div>
      </div>
    </div>
    <div v-else class="bn-empty">暂无技术统计</div>
  </div>
</template>

<script>
const TITLE_ORDER = {
  '总得分': 0,
  '助攻': 1,
  '篮板': 2,
  '抢断': 3,
  '盖帽': 4,
  '犯规': 5,
  '失误': 6,
}

/** 数值越小越好 */
const LOWER_IS_BETTER = new Set(['犯规', '失误'])

export default {
  props: {
    technicalData: {
      type: Array,
      default: () => []
    },
    homeTeamName: {
      type: String,
      default: ''
    },
    awayTeamName: {
      type: String,
      default: ''
    },
    homeTeamIcon: {
      type: String,
      default: ''
    },
    awayTeamIcon: {
      type: String,
      default: ''
    },
    homeScore: {
      type: [Number, String],
      default: null,
    },
    awayScore: {
      type: [Number, String],
      default: null,
    },
  },
  computed: {
    scoreRow() {
      const hs = this.homeScore
      const as = this.awayScore
      if (hs == null || as == null || hs === '' || as === '') return null
      const nh = Number(hs)
      const na = Number(as)
      if (Number.isNaN(nh) || Number.isNaN(na)) return null
      if (nh === -1 || na === -1) return null
      return {
        rowKey: 'total-score',
        title: '总得分',
        type: 'number',
        home: nh,
        away: na,
      }
    },
    sortedTechnical() {
      const raw = Array.isArray(this.technicalData) ? this.technicalData : []
      return [...raw].sort((a, b) => {
        const ta = (a && a.title) || ''
        const tb = (b && b.title) || ''
        const oa = Object.prototype.hasOwnProperty.call(TITLE_ORDER, ta) ? TITLE_ORDER[ta] : 999
        const ob = Object.prototype.hasOwnProperty.call(TITLE_ORDER, tb) ? TITLE_ORDER[tb] : 999
        if (oa !== ob) return oa - ob
        return 0
      })
    },
    displayRows() {
      const rows = []
      const sr = this.scoreRow
      if (sr) rows.push(sr)
      this.sortedTechnical.forEach((item, i) => {
        if (!item || !item.title) return
        if (sr && item.title === '总得分') return
        rows.push({ ...item, rowKey: `td-${item.title}-${i}` })
      })
      return rows
    },
  },
  methods: {
    isPercentRow(item) {
      const t = (item && item.type) || ''
      const title = (item && item.title) || ''
      return t === 'percent' || t === 'percentage' || /命中率$/.test(title)
    },
    num(item, side) {
      const raw = side === 'home' ? item.home : item.away
      const n = Number(raw)
      return Number.isNaN(n) ? 0 : n
    },
    rowDisplayValue(item, side) {
      if (this.isPercentRow(item)) {
        const v = side === 'home' ? item.home : item.away
        return `${v == null ? '' : v}%`
      }
      return side === 'home' ? item.home : item.away
    },
    /**
     * 从轨道正中央向两侧画条：左半区宽 50%，内条宽 (home/总和)×100%（相对左半区）且靠中右对齐；
     * 右半区对称，内条宽 (away/总和)×100% 且靠中左对齐。
     * 两色总可视宽度恒为轨道的 50%。
     */
    barRatioPair(item) {
      if (this.isPercentRow(item)) {
        const h = this.num(item, 'home')
        const a = this.num(item, 'away')
        const ph = Math.min(100, Math.max(0, h))
        const pa = Math.min(100, Math.max(0, a))
        const t = ph + pa
        if (t <= 0) return { h: 0.5, a: 0.5, sum: 1 }
        return { h: ph / t, a: pa / t, sum: t }
      }
      const h = this.num(item, 'home')
      const a = this.num(item, 'away')
      const sum = h + a
      if (sum <= 0) return { h: 0.5, a: 0.5, sum: 1 }
      return { h: h / sum, a: a / sum, sum }
    },
    barHalfSegStyle(item, side) {
      const { h, a } = this.barRatioPair(item)
      const ratio = side === 'home' ? h : a
      const pct = Math.round(ratio * 10000) / 100
      return { width: `${Math.min(100, Math.max(0, pct))}%` }
    },
    barSegClass(item, side) {
      const h = this.num(item, 'home')
      const a = this.num(item, 'away')
      if (this.isPercentRow(item)) {
        const tie = h === a
        if (tie) return 'bn-bar-seg--accent'
        if (side === 'home') return h > a ? 'bn-bar-seg--accent' : 'bn-bar-seg--muted'
        return a > h ? 'bn-bar-seg--accent' : 'bn-bar-seg--muted'
      }

      const title = item.title || ''
      const lowerBetter = LOWER_IS_BETTER.has(title)
      const tie = h === a
      if (tie) return 'bn-bar-seg--accent'

      if (lowerBetter) {
        if (side === 'home') return h < a ? 'bn-bar-seg--accent' : 'bn-bar-seg--muted'
        return a < h ? 'bn-bar-seg--accent' : 'bn-bar-seg--muted'
      }
      if (side === 'home') return h > a ? 'bn-bar-seg--accent' : 'bn-bar-seg--muted'
      return a > h ? 'bn-bar-seg--accent' : 'bn-bar-seg--muted'
    },
  },
}
</script>

<style scoped lang="scss">
.basketball-data {
  font-size: 0;
}

.bn-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  margin-bottom: 16px;
  background: #fff;
  border-radius: 10px;

  &__team {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-weight: 600;
    color: #292929;

    > * + * {
      margin-left: 8px;
    }

    &--home {
      justify-content: flex-end;
    }

    &--away {
      justify-content: flex-start;
      text-align: right;
    }
  }

  &__vs {
    flex-shrink: 0;
    width: 29px;
    height: 18px;
    object-fit: contain;
    margin-left: 20px;
    margin-right: 20px;
  }

  &__badge-img {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
  }

  &__name {
    min-width: 0;
    font-weight: 600;
    color: #292929;
  }
}

.bn-rows {
  padding: 0 2px;
}

.bn-row {
  padding-bottom: 16px;

  &__title {
    margin-bottom: 8px;
    text-align: center;
    color: #1a1a1a;
    font-weight: 500;
    font-size: 12px;
    white-space: nowrap;
  }

  &__bar-line {
    display: flex;
    align-items: center;
  }

  &__home-val,
  &__away-val {
    flex-shrink: 0;
    min-width: 18px;
    font-size: 14px;
    font-weight: 500;
    color: #606266;
  }

  &__home-val {
    text-align: left;
    margin-right: 7px;
  }

  &__away-val {
    text-align: right;
    margin-left: 7px;
  }
}

.bn-bar-track {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: row;
  height: 12px;
  overflow: hidden;
  background: #ebebeb;

  .bn-bar-half + .bn-bar-half {
    margin-left: 2px;
  }
}

.bn-bar-half {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: stretch;
  background: #ebebeb;

  &--home {
    justify-content: flex-end;
  }

  &--away {
    justify-content: flex-start;
  }
}

.bn-bar-seg {
  height: 100%;
  max-width: 100%;

  /** 胜方 */
  &--accent {
    background: #1677ff;
  }

  /** 负方 */
  &--muted {
    background: #aab1b9;
  }
}

.bn-empty {
  font-size: 13px;
  color: #9b9b9b;
  text-align: center;
  padding: 16px 0;
}

.overflow-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
